import { supabase } from '../../core/config/supabase';
import { LeagueEntity } from '../entities/LeagueEntity';
import { LeagueParticipantEntity } from '../entities/LeagueParticipantEntity';

export class LeagueDataSource {
  async create(league: Omit<LeagueEntity, 'id' | 'created_at' | 'updated_at'>): Promise<LeagueEntity> {
    const { data, error } = await supabase
      .from('leagues')
      .insert(league)
      .select()
      .single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error('No data returned from insert');

    return data as LeagueEntity;
  }

  async getById(id: string): Promise<LeagueEntity | null> {
    const { data, error } = await supabase
      .from('leagues')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw new Error(error.message);
    }

    return data as LeagueEntity;
  }

  async getUserLeagues(userId: string): Promise<LeagueEntity[]> {
    const { data, error } = await supabase
      .from('leagues')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw new Error(error.message);

    return (data as LeagueEntity[]) || [];
  }

  async update(id: string, league: Partial<LeagueEntity>): Promise<LeagueEntity> {
    const { data, error } = await supabase
      .from('leagues')
      .update({ ...league, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error('No data returned from update');

    return data as LeagueEntity;
  }

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('leagues')
      .delete()
      .eq('id', id);

    if (error) throw new Error(error.message);
  }

  // League Participants

  async addParticipant(participant: Omit<LeagueParticipantEntity, 'id' | 'created_at'>): Promise<LeagueParticipantEntity> {
    const { data, error } = await supabase
      .from('league_participants')
      .insert(participant)
      .select()
      .single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error('No data returned from insert');

    return data as LeagueParticipantEntity;
  }

  async getParticipants(leagueId: string): Promise<LeagueParticipantEntity[]> {
    const { data, error } = await supabase
      .from('league_participants')
      .select('*')
      .eq('league_id', leagueId)
      .order('created_at', { ascending: true });

    if (error) throw new Error(error.message);

    return (data as LeagueParticipantEntity[]) || [];
  }

  async removeParticipant(participantId: string): Promise<void> {
    const { error } = await supabase
      .from('league_participants')
      .delete()
      .eq('id', participantId);

    if (error) throw new Error(error.message);
  }

  async getPublicLeagues(limit: number, offset: number): Promise<LeagueEntity[]> {
    const { data, error } = await supabase
      .from('leagues')
      .select('*')
      .eq('is_public', true)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) throw new Error(error.message);

    return (data as LeagueEntity[]) || [];
  }

  async markTournamentsPublic(leagueId: string): Promise<void> {
    const { error } = await supabase
      .from('tournaments')
      .update({ is_public: true })
      .eq('league_id', leagueId);

    if (error) throw new Error(error.message);
  }
}
